import { useEffect, useState } from 'react';
import isDeepEqual from 'fast-deep-equal';
import { useTranslation } from 'react-i18next';
import { useTracking } from '../../../utils/tracking/TrackingContext';
import useAppState from '../../../AppStore';
import {
  UserMapConfiguration,
  useUserMapConfigurations,
  useAddUserMapConfiguration,
  useUpdateUserMapConfiguration,
  useRemoveUserMapConfiguration,
  useUpdateOrderUserMapConfiguration,
  MapDefinition,
} from './useUserMapConfigurations';

const isMapDefinitionEqual = (definitionA: MapDefinition, definitionB: MapDefinition) =>
  isDeepEqual(definitionA, definitionB);

export const useMapConfigurations = () => {
  const { t } = useTranslation('agisviewer');
  const { Tracking } = useTracking();

  const { data: configurations, isLoading, error } = useUserMapConfigurations();
  const addUserMapDefinition = useAddUserMapConfiguration();
  const updateUserMapDefinition = useUpdateUserMapConfiguration();
  const removeUserMapDefinition = useRemoveUserMapConfiguration();
  const updateOrderUserMapDefinition = useUpdateOrderUserMapConfiguration();

  const applyConfiguration = useAppState((state) => state.applyConfiguration);
  const selectedBasemapId = useAppState((state) => state.selectedBasemapId);
  const isBasemapVisible = useAppState((state) => state.isBasemapVisible);
  const basemapOpacity = useAppState((state) => state.basemapOpacity);
  const topicLayers = useAppState((state) => state.selectedTopicLayers);
  const topicLayerSettings = useAppState((state) => state.topicLayerSettings);
  const topicSubLayerSetting = useAppState((state) => state.topicSubLayerSetting);
  const addNotification = useAppState((state) => state.addNotification);

  const [sortedConfigurations, setSortedConfigurations] = useState<
    UserMapConfiguration[]
  >(configurations ?? []);

  useEffect(() => {
    setSortedConfigurations(configurations ?? []);
  }, [configurations]);

  const showNotification = (
    type: 'apply' | 'saved' | 'removed',
    configurationName: string,
  ) => {
    const notificationConfig = {
      apply: {
        title: t('configuration_apply_notification_title'),
        message: t('configuration_apply_notification_message', { configurationName }),
      },
      saved: {
        title: t('configuration_saved_notification_title'),
        message: t('configuration_saved_notification_message', { configurationName }),
      },
      removed: {
        title: t('configuration_removed_notification_title'),
        message: t('configuration_removed_notification_message', { configurationName }),
      },
    };

    addNotification({
      id: `configuration-${type}-${configurationName}`,
      title: notificationConfig[type].title,
      message: notificationConfig[type].message,
      type: 'information',
    });
  };

  const getMapDefinitionValue = (): MapDefinition => ({
    basemap: {
      selectedBasemapId,
      isBasemapVisible,
      basemapOpacity,
    },
    topicLayers,
    topicLayerSettings,
    topicSubLayerSetting,
  });

  const selectedConfiguration =
    sortedConfigurations.find((configuration) =>
      isMapDefinitionEqual(configuration.mapDefinitionValue, getMapDefinitionValue()),
    )?.name ?? null;

  const onApplyConfiguration = (configuration: UserMapConfiguration) => {
    Tracking.Configurations.applyConfiguration(configuration.name);
    applyConfiguration(configuration.mapDefinitionValue);
    showNotification('apply', configuration.name);
  };

  const onAddConfiguration = async () => {
    const name = new Date().toLocaleString();

    const newConfiguration = {
      name,
      mapDefinitionValue: getMapDefinitionValue(),
      createdAt: new Date(),
      createdBy: '',
    } as UserMapConfiguration;

    await Tracking.Configurations.addConfiguration(name);
    const response = await addUserMapDefinition.mutateAsync(newConfiguration);
    if (response.ok) showNotification('saved', newConfiguration.name);
  };

  const onUpdateConfiguration = async (id: string, newName: string) => {
    await Tracking.Configurations.updateConfigurationName(id, newName);
    const result = await updateUserMapDefinition.mutateAsync({ id, newName });
    if (result.ok) showNotification('saved', newName);
  };

  const onRemoveConfiguration = async (configuration: UserMapConfiguration) => {
    await Tracking.Configurations.removeConfiguration(configuration.name);
    const result = await removeUserMapDefinition.mutateAsync(configuration.id);
    if (result.ok) showNotification('removed', configuration.name);
  };

  const onUpdateOrder = (newItems: UserMapConfiguration[], newIds: string[]) => {
    setSortedConfigurations(newItems);
    updateOrderUserMapDefinition.mutate(newIds);
  };

  return {
    configurations: sortedConfigurations,
    isLoading,
    error,
    selectedConfiguration,
    onApplyConfiguration,
    onAddConfiguration,
    onUpdateConfiguration,
    onRemoveConfiguration,
    onUpdateOrder,
  };
};
